import type { WeatherType } from "./voxelTypes";

/**
 * The sound of the village.
 *
 * One bed plays at a time and the scene chooses which. That is the whole design, and it is chosen
 * deliberately over layering loops per effect: the weather here is a guild-wide fact derived from
 * the clock (see `weather.ts`), so everybody hearing the same single bed at the same moment is the
 * audible half of the same promise the sky makes.
 *
 * Adding the next sound is one line in `TRACKS`. A bed with no file recorded yet resolves to
 * silence rather than an error, so the table can name the whole set before the audio exists.
 *
 * Nothing here downloads or decodes anything until the member has both turned the volume up and
 * interacted with the page. Browsers refuse to start audio before a gesture, and a Discord Activity
 * that spent a megabyte of someone's data on a sound they had muted would deserve the uninstall.
 */

/** Every bed the village knows how to play, whether or not its file exists yet. */
export type AmbienceId = "day" | "rain" | "storm" | "snow";

type AmbienceTrack = {
  /** Served from `public/audio`, so the path is stable and the file is not bundled. */
  url: string;
  /**
   * Loudness relative to the member's setting. Recordings arrive at whatever level they were
   * mastered at, and this is where that is corrected — not by re-encoding the file.
   */
  gain: number;
};

const TRACKS: Partial<Record<AmbienceId, AmbienceTrack>> = {
  storm: { url: "/audio/thunderstorm.m4a", gain: 0.9 }
};

/** What the village is doing, as far as sound is concerned. */
export type AmbienceScene = {
  weather: WeatherType;
  /** Indoors the weather is still there, heard through a wall. */
  indoors: boolean;
};

/** Which bed belongs to a moment. */
export function bedFor(scene: AmbienceScene): AmbienceId {
  if (scene.weather === "storm") return "storm";
  if (scene.weather === "rain") return "rain";
  if (scene.weather === "snow") return "snow";
  return "day";
}

/**
 * Downloaded beds, kept for the life of the tab rather than the life of a system.
 *
 * Module scope on purpose. The village scene is rebuilt whenever its shape changes, and an
 * instance-level cache meant every rebuild re-downloaded the same megabyte — seven times over on a
 * cold start. The bytes are decoded per context because `decodeAudioData` detaches the buffer it is
 * handed, which is what the `slice` is for.
 */
const downloads = new Map<string, Promise<ArrayBuffer | null>>();

function download(url: string): Promise<ArrayBuffer | null> {
  const existing = downloads.get(url);
  if (existing) return existing;

  // A bed that will not load is a quieter village, not a broken one.
  const pending = fetch(url)
    .then((response) => (response.ok ? response.arrayBuffer() : Promise.reject(new Error(String(response.status)))))
    .catch((error: unknown) => {
      console.warn("Village ambience could not load", url, error);
      return null;
    });
  downloads.set(url, pending);
  return pending;
}

/** Long enough that a change of weather arrives rather than switches. */
const FADE_SECONDS = 2.5;
/** Where a wall cuts the top off the outdoors, in Hz. */
const MUFFLED_HZ = 620;
const OPEN_HZ = 20_000;

type Voice = {
  id: AmbienceId;
  source: AudioBufferSourceNode;
  gain: GainNode;
  filter: BiquadFilterNode;
};

export class AmbienceSystem {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private voice: Voice | null = null;
  private wanted: AmbienceId | null = null;
  private volume: number;
  private indoors = false;
  private disposed = false;
  private unlock: (() => void) | null = null;

  constructor(volume: number) {
    this.volume = clamp(volume);
  }

  /** What is actually coming out of the speakers, for checking the scene from the console. */
  public get state(): { bed: AmbienceId | null; playing: AmbienceId | null; device: string; muffled: boolean } {
    return {
      bed: this.wanted,
      playing: this.voice?.id ?? null,
      device: this.context?.state ?? "none",
      muffled: this.indoors
    };
  }

  /** 0 silences everything and releases the audio device; nothing is fetched while muted. */
  public setVolume(volume: number): void {
    this.volume = clamp(volume);
    if (this.disposed) return;
    if (this.master && this.context) {
      this.master.gain.setTargetAtTime(this.volume, this.context.currentTime, 0.08);
    }
    if (this.volume === 0) {
      void this.context?.suspend();
      return;
    }
    // Turning the volume back up is itself the cue to start, since the bed may never have loaded.
    void this.context?.resume();
    if (this.wanted) this.play(this.wanted);
  }

  /** Called whenever the weather or the member's whereabouts change. Cheap to call every frame. */
  public setScene(scene: AmbienceScene): void {
    if (this.disposed) return;
    const next = bedFor(scene);

    if (scene.indoors !== this.indoors) {
      this.indoors = scene.indoors;
      if (this.voice && this.context) {
        this.voice.filter.frequency.setTargetAtTime(
          this.indoors ? MUFFLED_HZ : OPEN_HZ,
          this.context.currentTime,
          0.25
        );
      }
    }

    if (next === this.wanted) return;
    this.wanted = next;
    this.play(next);
  }

  public destroy(): void {
    this.disposed = true;
    this.unlock?.();
    this.unlock = null;
    this.stopVoice(0);
    void this.context?.close().catch(() => undefined);
    this.context = null;
    this.master = null;
  }

  /**
   * The audio device, created on demand.
   *
   * A context made before a gesture starts suspended and stays that way, so one is made here and
   * a one-shot listener resumes it the first time the member touches anything.
   */
  private ensureContext(): AudioContext | null {
    if (this.disposed || typeof window === "undefined") return null;
    if (this.context) return this.context;

    const Constructor = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Constructor) return null;

    const context = new Constructor();
    const master = new GainNode(context, { gain: this.volume });
    master.connect(context.destination);
    this.context = context;
    this.master = master;

    if (context.state === "suspended") {
      const resume = () => void context.resume().catch(() => undefined);
      const events: (keyof WindowEventMap)[] = ["pointerdown", "keydown", "touchstart"];
      for (const event of events) window.addEventListener(event, resume, { once: true, passive: true });
      this.unlock = () => {
        for (const event of events) window.removeEventListener(event, resume);
      };
    }

    return context;
  }

  private async loadBuffer(context: AudioContext, url: string): Promise<AudioBuffer | null> {
    const bytes = await download(url);
    if (!bytes) return null;
    return context.decodeAudioData(bytes.slice(0)).catch((error: unknown) => {
      console.warn("Village ambience could not be decoded", url, error);
      return null;
    });
  }

  private play(id: AmbienceId): void {
    const track = TRACKS[id];
    if (!track || this.volume === 0) {
      // No recording for this weather yet, or nobody is listening. Let the old bed go.
      if (this.voice) this.stopVoice(FADE_SECONDS);
      return;
    }
    if (this.voice?.id === id) return;

    const context = this.ensureContext();
    if (!context || !this.master) return;

    void this.loadBuffer(context, track.url).then((buffer) => {
      // The weather can turn, or the member can leave, while a megabyte is in flight.
      if (!buffer || this.disposed || this.wanted !== id || this.volume === 0) return;
      if (this.voice?.id === id) return;
      const master = this.master;
      if (!master) return;

      this.stopVoice(FADE_SECONDS);

      const filter = new BiquadFilterNode(context, {
        type: "lowpass",
        frequency: this.indoors ? MUFFLED_HZ : OPEN_HZ
      });
      const gain = new GainNode(context, { gain: 0 });
      const source = new AudioBufferSourceNode(context, { buffer, loop: true });

      source.connect(filter).connect(gain).connect(master);
      gain.gain.linearRampToValueAtTime(track.gain, context.currentTime + FADE_SECONDS);
      source.start();

      this.voice = { id, source, gain, filter };
    });
  }

  /** Fades the current bed out and schedules it to stop once it is inaudible. */
  private stopVoice(fadeSeconds: number): void {
    const voice = this.voice;
    const context = this.context;
    this.voice = null;
    if (!voice) return;

    if (!context || fadeSeconds === 0) {
      voice.source.stop();
      voice.source.disconnect();
      return;
    }

    const endsAt = context.currentTime + fadeSeconds;
    voice.gain.gain.cancelScheduledValues(context.currentTime);
    voice.gain.gain.setValueAtTime(voice.gain.gain.value, context.currentTime);
    voice.gain.gain.linearRampToValueAtTime(0, endsAt);
    voice.source.stop(endsAt);
    voice.source.onended = () => voice.source.disconnect();
  }
}

function clamp(value: number): number {
  return Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0));
}
